// AI reports, backed by the Go backend's modules/report/* via the same
// /api-auth proxy prefix billingApi.ts / authApi.ts use.
//
// Generation is a job queue (backend zmist/report + cmd/report-worker),
// not an inline call:
// - POST   /channels/{nickname}/report-jobs          queue a job (owner only).
//   Body: {provider?, periodDays?, priority?, apiKey?, additionalChannelIds?}.
//   202 with the job.
// - GET    /channels/{nickname}/report-jobs          this channel's jobs.
// - DELETE /channels/{nickname}/report-jobs/{jobId}  stop a queued/running job.
// - GET    /report/ai-usage                          caller's free-allowance counter.
//
// The mutations need the double-submit CSRF header (same as billingApi's
// billingPost); the session rides the httpOnly cookie via credentials: 'include'.
import type { AiJobPriority, AIProvider, AiReportPeriodDays, AiReportUsage, AiUsageDetailed, ReportJob, ReportJobStatus } from '../types';
import { readCsrfCookie, reportHttpStatus } from './dataApi';

const API_BASE = import.meta.env.VITE_AUTH_API_BASE ?? '/api-auth';

async function unwrap<T>(response: Response): Promise<T> {
  let parsed: any = null;
  try {
    parsed = await response.json();
  } catch {
    // no JSON body
  }
  reportHttpStatus(response.status);
  const data = parsed?.data;
  if (response.ok && data && typeof data === 'object' && !('error' in data)) {
    return data as T;
  }
  const message =
    (data && typeof data === 'object' && data.error) || `Request failed (${response.status})`;
  throw new Error(String(message));
}

function csrfHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const csrf = readCsrfCookie();
  if (csrf) headers['X-CSRF-Token'] = csrf;
  return headers;
}

export async function fetchAiReportUsage(): Promise<AiReportUsage> {
  const response = await fetch(`${API_BASE}/report/ai-usage`, { credentials: 'include' });
  return unwrap<AiReportUsage>(response);
}

// GET /report/ai-usage-detailed - the caller's own per-provider call/
// token usage plus a summed "total" bucket (backend's
// report.AIUsageStats) - the "API usage" section in AI Settings.
export async function fetchAiUsageDetailed(): Promise<AiUsageDetailed> {
  const response = await fetch(`${API_BASE}/report/ai-usage-detailed`, { credentials: 'include' });
  const data = await unwrap<{ providers?: AiUsageDetailed }>(response);
  return data.providers ?? {};
}

// GET /ai/keys - per-provider: whether a key is saved and a masked hint,
// never the key itself (backend report.AiKeyStatus -> internal/report.
// ListUserKeyStatus, encrypted at rest - see internal/secrets' header
// comment). Account-wide, not per-channel.
export interface AiKeyStatus {
  provider: AIProvider;
  saved: boolean;
  masked: string;
}

export async function fetchAiKeyStatus(): Promise<AiKeyStatus[]> {
  const response = await fetch(`${API_BASE}/ai/keys`, { credentials: 'include' });
  const data = await unwrap<{ providers?: AiKeyStatus[] }>(response);
  return data.providers ?? [];
}

// POST /ai/keys/{provider} - save (apiKey non-empty) or clear (apiKey
// empty) the caller's own saved key for that provider. This is the
// cross-device persistence AiSettingsScreen.tsx's apiKeys used to lack -
// its own localStorage copy is unaffected and kept for immediate local
// echo; this is the backend source of truth a new device/browser reads
// back via fetchAiKeyStatus above.
export async function saveAiKey(provider: AIProvider, apiKey: string): Promise<void> {
  const response = await fetch(`${API_BASE}/ai/keys/${provider}`, {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders(),
    body: JSON.stringify({ apiKey }),
  });
  await unwrap<{ ok: boolean }>(response);
}

export interface ReportJobsPage {
  jobs: ReportJob[];
  total: number;
}

// GET /channels/{nickname}/report-jobs?offset=&limit= - one newest-first
// page of the channel's report jobs. total is the retained job count so
// the caller knows whether to keep paging.
export async function fetchReportJobs(
  nickname: string,
  opts: { offset?: number; limit?: number } = {}
): Promise<ReportJobsPage> {
  const params = new URLSearchParams();
  if (opts.offset != null) params.set('offset', String(opts.offset));
  if (opts.limit != null) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const response = await fetch(
    `${API_BASE}/channels/${encodeURIComponent(nickname)}/report-jobs${qs ? `?${qs}` : ''}`,
    { credentials: 'include' }
  );
  const data = await unwrap<{ jobs: ReportJob[]; total?: number }>(response);
  return { jobs: data.jobs ?? [], total: data.total ?? (data.jobs ?? []).length };
}

export interface EnqueueReportJobResult {
  job: ReportJob;
  usage: AiReportUsage;
}

export async function enqueueReportJob(
  nickname: string,
  opts: {
    provider: AIProvider;
    periodDays: AiReportPeriodDays;
    priority: AiJobPriority;
    apiKey?: string;
    // Up to 4 extra channels (own or public) to fold into this report as
    // a Multi-Channel Report - see backend/internal/report/access.go's
    // MaxAdditionalChannels. Any the caller can't actually read are
    // dropped server-side, not rejected.
    additionalChannelIds?: string[];
    // "questions" queues the "Ask questions" flow instead of the usual
    // report: `questions` (suggested and/or custom) is required then.
    mode?: 'questions';
    questions?: string[];
  }
): Promise<EnqueueReportJobResult> {
  const response = await fetch(`${API_BASE}/channels/${encodeURIComponent(nickname)}/report-jobs`, {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders(),
    body: JSON.stringify({
      provider: opts.provider,
      // Sent as a string: the backend transition takes periodDays as a
      // string (strconv.Atoi'd) - a raw JSON number arrives as a float64
      // the WSL engine won't bind to that parameter.
      periodDays: String(opts.periodDays),
      priority: opts.priority,
      apiKey: opts.apiKey?.trim() || undefined,
      additionalChannelIds: opts.additionalChannelIds?.length ? opts.additionalChannelIds : undefined,
      mode: opts.mode,
      questions: opts.questions?.length ? opts.questions : undefined,
    }),
  });
  return unwrap<EnqueueReportJobResult>(response);
}

// GET /channels/{nickname}/report-questions - this channel's stored
// question history (every question ever suggested or asked), read with
// no LLM call. The Reports screen calls this first so a channel that's
// already been asked about shows its past questions immediately.
export async function fetchChannelQuestions(nickname: string): Promise<string[]> {
  return (await fetchChannelQuestionState(nickname)).questions;
}

// The same GET plus which questions are switched OFF for "Ask questions"
// (stored per channel on the backend, so web and iOS agree).
export interface ChannelQuestionState {
  questions: string[];
  unchecked: string[];
}

export async function fetchChannelQuestionState(nickname: string): Promise<ChannelQuestionState> {
  const response = await fetch(`${API_BASE}/channels/${encodeURIComponent(nickname)}/report-questions`, {
    credentials: 'include',
  });
  const data = await unwrap<{ questions?: string[]; unchecked?: string[] }>(response);
  return { questions: data.questions ?? [], unchecked: data.unchecked ?? [] };
}

// POST /channels/{nickname}/report-questions/selection {unchecked} - stores
// which questions are switched off. Returns what the backend kept.
export async function setQuestionSelection(nickname: string, unchecked: string[]): Promise<string[]> {
  const response = await fetch(`${API_BASE}/channels/${encodeURIComponent(nickname)}/report-questions/selection`, {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders(),
    body: JSON.stringify({ unchecked }),
  });
  const data = await unwrap<{ unchecked?: string[] }>(response);
  return data.unchecked ?? [];
}

// POST /channels/{nickname}/report-sections {reportId, hidden} - switches
// "## " sections of one posted report off/on. The public pages then show
// the report without the hidden sections. Returns the stored list.
export async function setReportSections(nickname: string, reportId: string, hidden: string[]): Promise<string[]> {
  const response = await fetch(`${API_BASE}/channels/${encodeURIComponent(nickname)}/report-sections`, {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders(),
    body: JSON.stringify({ reportId, hidden }),
  });
  const data = await unwrap<{ hiddenSections?: string[] }>(response);
  return data.hiddenSections ?? [];
}

// POST /channels/{nickname}/report-questions/suggest - the "Ask AI again"
// action. Queued (mode "suggest"), same as every other AI call in this
// app (backend/internal/report/jobs.go's EnqueueSuggestJob) - 202 with
// the queued job, not the questions themselves. The result is also
// appended to the channel's stored history server-side
// (fetchChannelQuestions picks it up next time with no further LLM
// call).
async function enqueueSuggestQuestionsJob(
  nickname: string,
  opts: {
    provider: AIProvider;
    periodDays: AiReportPeriodDays;
    apiKey?: string;
    additionalChannelIds?: string[];
  }
): Promise<ReportJob> {
  const response = await fetch(
    `${API_BASE}/channels/${encodeURIComponent(nickname)}/report-questions/suggest`,
    {
      method: 'POST',
      credentials: 'include',
      headers: csrfHeaders(),
      body: JSON.stringify({
        provider: opts.provider,
        periodDays: String(opts.periodDays),
        apiKey: opts.apiKey?.trim() || undefined,
        additionalChannelIds: opts.additionalChannelIds?.length ? opts.additionalChannelIds : undefined,
      }),
    }
  );
  const data = await unwrap<{ job: ReportJob }>(response);
  return data.job;
}

const JOB_TERMINAL: ReportJobStatus[] = ['done', 'failed', 'stopped'];

// Polls GET /channels/{nickname}/report-jobs for jobId until it reaches a
// terminal status (same job list the Reports screen's Cue "rj" relay /
// 5s fallback poll already watches - this is a standalone poll since a
// caller here, e.g. ChannelQuestionsSection, doesn't share that screen's
// store state). ~1.5s between checks, capped at ~45s - a suggestion is a
// single short LLM call, not a full report, so it should never need
// anywhere near that long; a timeout throws rather than hanging the
// caller's "Thinking…" state forever.
async function pollReportJob(nickname: string, jobId: string): Promise<ReportJob> {
  for (let attempt = 0; attempt < 30; attempt++) {
    const { jobs } = await fetchReportJobs(nickname, { limit: 20 });
    const job = jobs.find((j) => j.id === jobId);
    if (job && JOB_TERMINAL.includes(job.status)) return job;
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
  throw new Error('Timed out waiting for the suggestion job to finish.');
}

export async function suggestReportQuestions(
  nickname: string,
  opts: {
    provider: AIProvider;
    periodDays: AiReportPeriodDays;
    apiKey?: string;
    // Extra channels (own or public) to fold into the suggestion - the
    // same Multi-Channel Report selection enqueueReportJob takes.
    additionalChannelIds?: string[];
  }
): Promise<string[]> {
  const queued = await enqueueSuggestQuestionsJob(nickname, opts);
  const finished = await pollReportJob(nickname, queued.id);
  if (finished.status !== 'done') {
    throw new Error(finished.error || 'Could not suggest questions.');
  }
  return finished.questions ?? [];
}

// POST /channels/{nickname}/report-questions {question} - adds one
// question straight to the channel's stored history, no LLM call or
// report job (unlike asking it via enqueueReportJob). Used by the AI
// Settings "Manage questions" editor's plain add/rename. Returns the
// updated list.
export async function addReportQuestion(nickname: string, question: string): Promise<string[]> {
  const response = await fetch(`${API_BASE}/channels/${encodeURIComponent(nickname)}/report-questions`, {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders(),
    body: JSON.stringify({ question }),
  });
  const data = await unwrap<{ questions?: string[] }>(response);
  return data.questions ?? [];
}

// DELETE /channels/{nickname}/report-questions {question} - permanently
// drops one question from the channel's stored history (exact string
// match). Returns the updated list.
export async function removeReportQuestion(nickname: string, question: string): Promise<string[]> {
  const response = await fetch(`${API_BASE}/channels/${encodeURIComponent(nickname)}/report-questions`, {
    method: 'DELETE',
    credentials: 'include',
    headers: csrfHeaders(),
    body: JSON.stringify({ question }),
  });
  const data = await unwrap<{ questions?: string[] }>(response);
  return data.questions ?? [];
}

// POST /channels/{nickname}/report-questions/rename {oldQuestion, newQuestion} -
// edits one stored question in place (it keeps its position). Returns the
// updated list.
export async function renameReportQuestion(nickname: string, oldQuestion: string, newQuestion: string): Promise<string[]> {
  const response = await fetch(`${API_BASE}/channels/${encodeURIComponent(nickname)}/report-questions/rename`, {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders(),
    body: JSON.stringify({ oldQuestion, newQuestion }),
  });
  const data = await unwrap<{ questions?: string[] }>(response);
  return data.questions ?? [];
}

// ---- report prompts (ZMAP-configurable, per-channel owner overrides) ------
//
// Three layers, highest priority first: a channel owner's own override,
// an operator-set ZMAP default, then the compiled-in default - see
// backend/internal/report/prompts.go's header comment. {{token}} inside a
// template is substituted at generation time (channelTitle, periodDays,
// etc. - see PROMPT_KINDS below's companion `tokens` response field);
// an unrecognized {{token}} is left as literal text, never an error.
export const REPORT_PROMPT_KINDS = ['standard', 'multi', 'questions', 'suggest'] as const;
export type ReportPromptKind = (typeof REPORT_PROMPT_KINDS)[number];

export const REPORT_PROMPT_KIND_LABEL: Record<ReportPromptKind, string> = {
  standard: 'Single-channel report',
  multi: 'Multi-Channel Report',
  questions: 'Ask questions (answers)',
  suggest: 'Ask questions (suggestions)',
};

export interface ReportPromptToken {
  name: string;
  description: string;
}

export interface ReportPrompts {
  defaults: Record<ReportPromptKind, string>;
  zmapDefaults: Partial<Record<ReportPromptKind, string>>;
  channelOverrides: Partial<Record<ReportPromptKind, string>>;
  tokens: ReportPromptToken[];
}

// GET /channels/{nickname}/report-prompts
export async function fetchReportPrompts(nickname: string): Promise<ReportPrompts> {
  const response = await fetch(`${API_BASE}/channels/${encodeURIComponent(nickname)}/report-prompts`, {
    credentials: 'include',
  });
  return unwrap<ReportPrompts>(response);
}

// POST /channels/{nickname}/report-prompts {kind, template} - template ""
// clears this channel's override, reverting to the ZMAP/compiled-in
// default. Returns the saved (or now-empty) template.
export async function saveReportPrompt(nickname: string, kind: ReportPromptKind, template: string): Promise<string> {
  const response = await fetch(`${API_BASE}/channels/${encodeURIComponent(nickname)}/report-prompts`, {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders(),
    body: JSON.stringify({ kind, template }),
  });
  const data = await unwrap<{ template?: string }>(response);
  return data.template ?? '';
}

export async function cancelReportJob(nickname: string, jobId: string): Promise<ReportJob> {
  const response = await fetch(
    `${API_BASE}/channels/${encodeURIComponent(nickname)}/report-jobs/${encodeURIComponent(jobId)}`,
    { method: 'DELETE', credentials: 'include', headers: csrfHeaders() }
  );
  const { job } = await unwrap<{ job: ReportJob }>(response);
  return job;
}

// ---- automatic reports (scheduled digests + auto-refresh) ------------------
//
// One schedule per channel (backend internal/report/schedule.go):
// - GET    /channels/{nickname}/report-schedule   {schedule: {...} | null}
// - POST   /channels/{nickname}/report-schedule   create/replace; mode "off" removes it
// - DELETE /channels/{nickname}/report-schedule   remove
// A schedule never carries an API key (it runs on an operator shared
// key), so there is no apiKey field here by design.
export const REPORT_SCHEDULE_MODES = ['off', 'daily', 'weekly', 'on_new'] as const;
export type ReportScheduleMode = (typeof REPORT_SCHEDULE_MODES)[number];

export interface ReportSchedule {
  channelId: string;
  mode: Exclude<ReportScheduleMode, 'off'>;
  provider: AIProvider;
  periodDays: number;
  hour: number; // 0-23, daily/weekly
  weekday: number; // 0 = Sunday, weekly
  timezone: string; // IANA
  debounceMinutes: number; // on_new
  updatedAt: number;
  lastRunAt: number;
  lastJobId?: string;
  lastResult?: string; // "queued" | "skipped: <why>"
}

export interface ReportScheduleInput {
  mode: ReportScheduleMode;
  provider?: AIProvider;
  periodDays?: number;
  hour?: number;
  weekday?: number;
  timezone?: string;
  debounceMinutes?: number;
}

export async function fetchReportSchedule(nickname: string): Promise<ReportSchedule | null> {
  const response = await fetch(`${API_BASE}/channels/${encodeURIComponent(nickname)}/report-schedule`, {
    credentials: 'include',
  });
  const data = await unwrap<{ schedule: ReportSchedule | null }>(response);
  return data.schedule ?? null;
}

// Returns the stored schedule, or null when mode was "off" (schedule removed).
export async function saveReportSchedule(nickname: string, input: ReportScheduleInput): Promise<ReportSchedule | null> {
  const response = await fetch(`${API_BASE}/channels/${encodeURIComponent(nickname)}/report-schedule`, {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders(),
    body: JSON.stringify(input),
  });
  const data = await unwrap<{ schedule: ReportSchedule | null }>(response);
  return data.schedule ?? null;
}
